import './bootstrap';
import '../css/app.css';

import ErrorBoundary from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import AdminGuestLayout from '@/Layouts/AdminGuestLayout';
import AdminLayout from '@/Layouts/AdminLayout';
import AppLayout from '@/Layouts/AppLayout';
import { CategoryProvider } from '@/lib/CategoryContext';
import { reportClientError } from '@/lib/reportClientError';
import { createInertiaApp, ResolvedComponent, router } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import React from 'react';
import { createRoot } from 'react-dom/client';

// Errors outside React's render tree (event handlers, async code, third-party
// scripts) never reach ErrorBoundary — this is the only way to see those in
// production, where there's no console to check.
window.addEventListener('error', (event) => {
  const error: unknown = event.error;
  reportClientError({
    message: event.message,
    stack: error instanceof Error ? error.stack : undefined,
  });
});
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as unknown;
  reportClientError({
    message:
      reason instanceof Error
        ? reason.message
        : `Unhandled rejection: ${String(reason)}`,
    stack: reason instanceof Error ? reason.stack : undefined,
  });
});

// A 500 (or any non-Inertia response — a raw SQL/exception page) during a
// CRUD submit never reaches the handlers above: Inertia shows its own error
// dialog for it instead of throwing. Admin create/edit/delete is where these
// mostly happen, and today they leave no signal beyond that dialog.
router.on('httpException', (event) => {
  reportClientError({
    message: `Server error (HTTP ${event.detail.response.status}) on ${window.location.href}`,
  });
});

const appName =
  (import.meta.env.VITE_APP_NAME as string) || 'Samuthrika Academy';

void createInertiaApp({
  // Public pages get a complete title from SeoService (brand included), so it
  // passes straight through. Admin and auth pages pass a bare label such as
  // 'Edit Course' and still get the brand appended here.
  title: (title) => (title.includes(appName) ? title : `${title} - ${appName}`),
  resolve: (name): Promise<ResolvedComponent> =>
    resolvePageComponent<{ default: ResolvedComponent }>(
      `./Pages/${name}.tsx`,
      import.meta.glob<{ default: ResolvedComponent }>('./Pages/**/*.tsx')
    ).then((page) => {
      page.default.layout = name.startsWith('Admin')
        ? name.startsWith('Admin/Auth')
          ? (page: React.ReactNode) => (
              <AdminGuestLayout>{page}</AdminGuestLayout>
            )
          : (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>
        : (page: React.ReactNode) => <AppLayout>{page}</AppLayout>;
      return page.default;
    }),
  setup({ el, App, props }) {
    const root = createRoot(el);

    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <CategoryProvider>
            <App {...props} />
            <Toaster position="top-center" richColors />
          </CategoryProvider>
        </ErrorBoundary>
      </React.StrictMode>
    );
  },
  progress: {
    // Inertia's own stylesheet exposes only a single colour on a flat 2px bar.
    // Opting out of it moves the whole indicator into app.css (#nprogress),
    // which is also where the reasoning for its colour lives — `color` below
    // would have no effect at all with includeCSS off.
    includeCSS: false,
    showSpinner: false,
  },
});
